// src/components/admin/sales/SalesHistoryTab.tsx
// Histórico Detalhado de Vendas - REFATORADO GRAIA

import React, { useState } from 'react';
import { useSalesHistory, useSalesVendors, useSalesProducts, useInvalidate } from '../../../hooks/useAdminQueries';
import {
    FileText,
    Download,
    Filter,
    Calendar,
    DollarSign,
    TrendingUp,
    Package,
    X,
    Plus,
    Trash2,
    AlertTriangle,
    User,
    ShoppingCart
} from 'lucide-react';
import './SalesHistoryTab.css';

interface SalesHistoryTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const SalesHistoryTab: React.FC<SalesHistoryTabProps> = ({ onShowToast }) => {

    // Filtros
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterPeriod, setFilterPeriod] = useState<string>('all');
    const [filterVendor, setFilterVendor] = useState<string>('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const { data, isLoading } = useSalesHistory({
        status: filterStatus,
        period: filterPeriod,
        vendorId: filterVendor,
        startDate,
        endDate,
    });
    const { data: vendorsData } = useSalesVendors();
    const { data: productsData } = useSalesProducts();
    const invalidate = useInvalidate();

    const sales = data?.sales ?? [];
    const stats = data?.stats ?? null;
    const vendors = vendorsData?.vendors ?? [];
    const products = (productsData?.products ?? []).filter((p: any) => p.plans.length > 0);
    const loading = isLoading;

    // Modal de registro manual
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [registerForm, setRegisterForm] = useState({
        customerName: '',
        customerEmail: '',
        productId: '',
        planIndex: 0,
        paymentMethod: 'dinheiro',
        notes: ''
    });
    const [registering, setRegistering] = useState(false);

    // Modal de confirmação de exclusão
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState<{ id: string; customer: string; amount: number } | null>(null);
    const [deleting, setDeleting] = useState(false);



    const handleRegisterSale = async () => {
        if (!registerForm.customerName.trim() || !registerForm.customerEmail.trim() || !registerForm.productId) {
            onShowToast('Preencha todos os campos obrigatórios', 'error');
            return;
        }

        try {
            setRegistering(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/admin/sales/register-manual', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(registerForm)
            });

            if (response.ok) {
                onShowToast('Venda registrada com sucesso!', 'success');
                setShowRegisterModal(false);
                setRegisterForm({
                    customerName: '',
                    customerEmail: '',
                    productId: '',
                    planIndex: 0,
                    paymentMethod: 'dinheiro',
                    notes: ''
                });
                invalidate.salesHistory();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao registrar venda', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao registrar venda', 'error');
        } finally {
            setRegistering(false);
        }
    };

    const openDeleteModal = (saleId: string, customerName: string, amount: number) => {
        setSaleToDelete({ id: saleId, customer: customerName, amount });
        setShowDeleteModal(true);
    };

    const handleDeleteSale = async () => {
        if (!saleToDelete) return;

        try {
            setDeleting(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/sales/${saleToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Venda excluída com sucesso', 'success');
                setShowDeleteModal(false);
                setSaleToDelete(null);
                invalidate.salesHistory();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao excluir venda', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao excluir venda', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('accessToken');

            const params = new URLSearchParams();
            if (filterStatus !== 'all') params.append('status', filterStatus);
            if (filterVendor !== 'all') params.append('vendorId', filterVendor);
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const response = await fetch(
                `/api/admin/sales/sales-history/export?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `vendas-graia-${Date.now()}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                onShowToast('CSV exportado com sucesso!', 'success');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao exportar CSV', 'error');
        }
    };

    const clearFilters = () => {
        setFilterStatus('all');
        setFilterPeriod('all');
        setFilterVendor('all');
        setStartDate('');
        setEndDate('');
    };

    const formatCurrency = (cents: number) => {
        return `R$ ${(cents / 100).toFixed(2)}`;
    };

    const formatDateCompact = (dateStr: string) => {
        const date = new Date(dateStr);
        const dateStr2 = date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
        const timeStr = date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
        return { date: dateStr2, time: timeStr };
    };

    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { label: string; className: string }> = {
            approved: { label: 'Aprov.', className: 'sh-status-approved' },
            pending: { label: 'Pend.', className: 'sh-status-pending' },
            rejected: { label: 'Rejeit.', className: 'sh-status-rejected' },
            refunded: { label: 'Reemb.', className: 'sh-status-refunded' }
        };

        const info = statusMap[status] || { label: status, className: '' };

        return <span className={`sh-status-badge ${info.className}`}>{info.label}</span>;
    };

    const selectedProduct = products.find((p: any) => p._id === registerForm.productId);

    if (loading) {
        return (
            <div className="sh-loading">
                <div className="sh-spinner"></div>
                <p>Carregando histórico de vendas...</p>
            </div>
        );
    }

    return (
        <div className="sh-container">
            {/* HEADER */}
            <div className="sh-header">
                <div>
                    <h2>Histórico de Vendas</h2>
                    <p className="sh-subtitle">
                        Visualize e exporte todas as transações
                    </p>
                </div>
                <div className="sh-header-actions">
                    <button className="sh-btn-register" onClick={() => setShowRegisterModal(true)}>
                        <Plus size={18} />
                        Registrar Venda
                    </button>
                    <button className="sh-btn-export" onClick={handleExport}>
                        <Download size={18} />
                        Exportar CSV
                    </button>
                </div>
            </div>

            {/* STATS CARDS */}
            {stats && (
                <div className="sh-stats-grid">
                    <div className="sh-stat-card">
                        <div className="sh-stat-icon" style={{ background: '#3b82f6' }}>
                            <FileText size={24} />
                        </div>
                        <div className="sh-stat-info">
                            <span className="sh-stat-label">Total de Vendas</span>
                            <span className="sh-stat-value">{stats.totalSales}</span>
                            <span className="sh-stat-detail">{stats.approvedSales} aprovadas</span>
                        </div>
                    </div>

                    <div className="sh-stat-card">
                        <div className="sh-stat-icon" style={{ background: '#003BD1' }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="sh-stat-info">
                            <span className="sh-stat-label">Receita Total</span>
                            <span className="sh-stat-value">{formatCurrency(stats.totalRevenue)}</span>
                            <span className="sh-stat-detail">Vendas aprovadas</span>
                        </div>
                    </div>

                    <div className="sh-stat-card sh-highlight">
                        <div className="sh-stat-icon" style={{ background: '#10b981' }}>
                            <TrendingUp size={24} />
                        </div>
                        <div className="sh-stat-info">
                            <span className="sh-stat-label">Lucro Líquido</span>
                            <span className="sh-stat-value">{formatCurrency(stats.netProfit)}</span>
                            <span className="sh-stat-detail">Receita - Comissões</span>
                        </div>
                    </div>

                    <div className="sh-stat-card">
                        <div className="sh-stat-icon" style={{ background: '#f59e0b' }}>
                            <Package size={24} />
                        </div>
                        <div className="sh-stat-info">
                            <span className="sh-stat-label">Comissões Pagas</span>
                            <span className="sh-stat-value">{formatCurrency(stats.totalCommission)}</span>
                            <span className="sh-stat-detail">Para vendedores</span>
                        </div>
                    </div>
                </div>
            )}

            {/* FILTROS - ALINHADOS EM LINHA RETA */}
            <div className="sh-filters">
                <div className="sh-filters-row">
                    <div className="sh-filter-group">
                        <Filter size={16} />
                        <span>Status:</span>
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="approved">Aprovado</option>
                            <option value="pending">Pendente</option>
                            <option value="rejected">Rejeitado</option>
                            <option value="refunded">Reembolsado</option>
                        </select>
                    </div>

                    <div className="sh-filter-group">
                        <Calendar size={16} />
                        <span>Período:</span>
                        <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="today">Hoje</option>
                            <option value="week">Última semana</option>
                            <option value="month">Último mês</option>
                            <option value="year">Último ano</option>
                        </select>
                    </div>

                    <div className="sh-filter-group">
                        <User size={16} />
                        <span>Vendedor:</span>
                        <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="admin">Admin</option>
                            {vendors.map((vendor: any) => (
                                <option key={vendor._id} value={vendor._id}>
                                    {vendor.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="sh-filter-group">
                        <span>De:</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="sh-date-input"
                        />
                    </div>

                    <div className="sh-filter-group">
                        <span>Até:</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="sh-date-input"
                        />
                    </div>

                    {(filterStatus !== 'all' || filterPeriod !== 'all' || filterVendor !== 'all' || startDate || endDate) && (
                        <button className="sh-btn-clear-filters" onClick={clearFilters}>
                            <X size={16} />
                            Limpar
                        </button>
                    )}
                </div>
            </div>

            {/* TABELA - SEM SCROLL HORIZONTAL */}
            {sales.length === 0 ? (
                <div className="sh-empty">
                    <FileText size={64} />
                    <h3>Nenhuma venda encontrada</h3>
                    <p>Ajuste os filtros ou aguarde novas vendas</p>
                </div>
            ) : (
                <div className="sh-table-wrapper">
                    <table className="sh-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Cliente</th>
                                <th>Produto</th>
                                <th>Valor</th>
                                <th>Desc.</th>
                                <th>Total</th>
                                <th>Vendedor</th>
                                <th>Comis.</th>
                                <th>Status</th>
                                <th className="sh-actions-col">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((sale: any) => {
                                const { date, time } = formatDateCompact(sale.date);
                                return (
                                    <tr key={sale._id}>
                                        <td className="sh-date-cell">
                                            <div className="sh-date-wrapper">
                                                <span className="sh-date">{date}</span>
                                                <span className="sh-time">{time}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="sh-customer-cell">
                                                <strong>{sale.customer.name}</strong>
                                                <span className="sh-customer-email">{sale.customer.email}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="sh-product-cell">
                                                <span className="sh-product-name">{sale.product}</span>
                                                {sale.couponCode && (
                                                    <span className="sh-coupon-badge">{sale.couponCode}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="sh-value">
                                            {formatCurrency(sale.originalPrice)}
                                        </td>
                                        <td className="sh-discount">
                                            {sale.discount > 0 ? `-${formatCurrency(sale.discount)}` : '-'}
                                        </td>
                                        <td className="sh-value sh-total">
                                            {formatCurrency(sale.finalPrice)}
                                        </td>
                                        <td>
                                            {sale.vendor ? (
                                                <span className={sale.vendor.isAdmin ? 'sh-vendor-admin' : 'sh-vendor-name'}>
                                                    {sale.vendor.name}
                                                </span>
                                            ) : (
                                                <span className="sh-vendor-admin">Admin</span>
                                            )}
                                        </td>
                                        <td className="sh-commission">
                                            {sale.vendorCommission > 0
                                                ? formatCurrency(sale.vendorCommission)
                                                : '-'}
                                        </td>
                                        <td>{getStatusBadge(sale.paymentStatus)}</td>
                                        <td className="sh-actions-col">
                                            <button
                                                className="sh-btn-delete"
                                                onClick={() => openDeleteModal(sale._id, sale.customer.name, sale.finalPrice)}
                                                title="Excluir venda"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL: REGISTRAR VENDA MANUAL */}
            {showRegisterModal && (
                <div className="sh-modal-overlay" onClick={() => !registering && setShowRegisterModal(false)}>
                    <div className="sh-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="sh-modal-header">
                            <h3>Registrar Venda Manual</h3>
                            <button
                                className="sh-btn-close-modal"
                                onClick={() => setShowRegisterModal(false)}
                                disabled={registering}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="sh-modal-body">
                            <p className="sh-modal-hint">
                                Use este formulário para registrar vendas realizadas em <strong>dinheiro</strong>, <strong>transferência bancária</strong> ou outros métodos manuais.
                            </p>

                            {/* Informações do Cliente */}
                            <div className="sh-form-section">
                                <h4>
                                    <User size={16} />
                                    Informações do Cliente
                                </h4>

                                <div className="sh-form-group">
                                    <label>Nome Completo *</label>
                                    <input
                                        type="text"
                                        value={registerForm.customerName}
                                        onChange={(e) => setRegisterForm({ ...registerForm, customerName: e.target.value })}
                                        placeholder="Ex: João Silva"
                                        className="sh-form-input"
                                        disabled={registering}
                                    />
                                </div>

                                <div className="sh-form-group">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        value={registerForm.customerEmail}
                                        onChange={(e) => setRegisterForm({ ...registerForm, customerEmail: e.target.value })}
                                        placeholder="joao@email.com"
                                        className="sh-form-input"
                                        disabled={registering}
                                    />
                                </div>
                            </div>

                            {/* Informações do Produto */}
                            <div className="sh-form-section">
                                <h4>
                                    <ShoppingCart size={16} />
                                    Informações do Produto
                                </h4>

                                <div className="sh-form-group">
                                    <label>Produto *</label>
                                    <select
                                        value={registerForm.productId}
                                        onChange={(e) => setRegisterForm({ ...registerForm, productId: e.target.value, planIndex: 0 })}
                                        className="sh-form-select"
                                        disabled={registering}
                                    >
                                        <option value="">Selecione um produto</option>
                                        {products.map((product: any) => (
                                            <option key={product._id} value={product._id}>
                                                {product.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedProduct && (
                                    <div className="sh-form-group">
                                        <label>Plano *</label>
                                        <select
                                            value={registerForm.planIndex}
                                            onChange={(e) => setRegisterForm({ ...registerForm, planIndex: parseInt(e.target.value) })}
                                            className="sh-form-select"
                                            disabled={registering}
                                        >
                                            {selectedProduct.plans.map((plan: { durationMonths: number | null; priceCents: number }, idx: number) => (
                                                <option key={idx} value={idx}>
                                                    {plan.durationMonths === null ? 'Vitalício' : `${plan.durationMonths} ${plan.durationMonths === 1 ? 'mês' : 'meses'}`} - {formatCurrency(plan.priceCents)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="sh-form-group">
                                    <label>Método de Pagamento *</label>
                                    <select
                                        value={registerForm.paymentMethod}
                                        onChange={(e) => setRegisterForm({ ...registerForm, paymentMethod: e.target.value })}
                                        className="sh-form-select"
                                        disabled={registering}
                                    >
                                        <option value="dinheiro">Dinheiro</option>
                                        <option value="transferencia">Transferência Bancária</option>
                                        <option value="pix">PIX</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="outro">Outro</option>
                                    </select>
                                </div>

                                <div className="sh-form-group">
                                    <label>Observações</label>
                                    <textarea
                                        value={registerForm.notes}
                                        onChange={(e) => setRegisterForm({ ...registerForm, notes: e.target.value })}
                                        placeholder="Observações adicionais sobre a venda..."
                                        className="sh-form-textarea"
                                        rows={3}
                                        disabled={registering}
                                    />
                                </div>
                            </div>

                            {selectedProduct && (
                                <div className="sh-sale-summary">
                                    <h4>Resumo da Venda</h4>
                                    <div className="sh-summary-row">
                                        <span>Produto:</span>
                                        <strong>{selectedProduct.title}</strong>
                                    </div>
                                    <div className="sh-summary-row">
                                        <span>Plano:</span>
                                        <strong>
                                            {selectedProduct.plans[registerForm.planIndex]?.durationMonths === null
                                                ? 'Vitalício'
                                                : `${selectedProduct.plans[registerForm.planIndex]?.durationMonths} meses`}
                                        </strong>
                                    </div>
                                    <div className="sh-summary-row sh-summary-total">
                                        <span>Valor Total:</span>
                                        <strong>{formatCurrency(selectedProduct.plans[registerForm.planIndex]?.priceCents || 0)}</strong>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="sh-modal-footer">
                            <button
                                className="sh-btn-cancel"
                                onClick={() => setShowRegisterModal(false)}
                                disabled={registering}
                            >
                                Cancelar
                            </button>
                            <button
                                className="sh-btn-confirm"
                                onClick={handleRegisterSale}
                                disabled={registering || !registerForm.productId}
                            >
                                {registering ? (
                                    <>
                                        <div className="sh-spinner-small"></div>
                                        Registrando...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={16} />
                                        Registrar Venda
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: CONFIRMAR EXCLUSÃO */}
            {showDeleteModal && saleToDelete && (
                <div className="sh-modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
                    <div className="sh-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="sh-modal-header">
                            <h3>Confirmar Exclusão</h3>
                            <button
                                className="sh-btn-close-modal"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="sh-modal-body">
                            <div className="sh-delete-icon">
                                <AlertTriangle size={48} />
                            </div>

                            <p className="sh-delete-text">
                                Tem certeza que deseja <strong>EXCLUIR PERMANENTEMENTE</strong> esta venda?
                            </p>

                            <div className="sh-delete-details">
                                <div className="sh-detail-row">
                                    <span>Cliente:</span>
                                    <strong>{saleToDelete.customer}</strong>
                                </div>
                                <div className="sh-detail-row">
                                    <span>Valor:</span>
                                    <strong>{formatCurrency(saleToDelete.amount)}</strong>
                                </div>
                            </div>

                            <div className="sh-warning-box">
                                <AlertTriangle size={14} />
                                <span>
                                    Esta ação é <strong>irreversível</strong>. Todos os registros desta venda serão removidos, incluindo:
                                    receita total, comissões, estatísticas e acesso do cliente ao produto.
                                </span>
                            </div>
                        </div>

                        <div className="sh-modal-footer">
                            <button
                                className="sh-btn-cancel"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                Cancelar
                            </button>
                            <button
                                className="sh-btn-danger"
                                onClick={handleDeleteSale}
                                disabled={deleting}
                            >
                                {deleting ? (
                                    <>
                                        <div className="sh-spinner-small"></div>
                                        Excluindo...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Excluir Venda
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesHistoryTab;
